/**
 * AppSecurityService.js
 * Independent per-application security lock management for CipherVault.
 * Default security = OFF for every app.
 */

const STORAGE_PREFIX = 'ciphervault_app_security_';
const UNLOCK_PREFIX = 'pm_app_unlock_';

export const ALL_CIPHERVAULT_APPS = [
  { slug: 'passwords', name: 'Password Vault', defaultCategory: 'Core Vault' },
  { slug: 'notes', name: 'Secure Notes', defaultCategory: 'Core Vault' },
  { slug: 'identities', name: 'Identities', defaultCategory: 'Identity' },
  { slug: 'cards', name: 'Cards', defaultCategory: 'Finance' },
  { slug: 'documents', name: 'Documents', defaultCategory: 'Storage' },
  { slug: 'reminders', name: 'Reminders', defaultCategory: 'Productivity' },
  { slug: 'tasks', name: 'Tasks', defaultCategory: 'Productivity' },
  { slug: 'notepad', name: 'Notepad', defaultCategory: 'Productivity' },
  { slug: 'ssh-keys', name: 'SSH Keys', defaultCategory: 'Developer' },
  { slug: 'wifi', name: 'Wi-Fi', defaultCategory: 'Network' },
  { slug: 'bookmarks', name: 'Bookmarks', defaultCategory: 'Productivity' },
  { slug: 'recovery-codes', name: 'Recovery Codes', defaultCategory: 'Security' },
  { slug: 'expense-tracker', name: 'Expense Tracker', defaultCategory: 'Finance' },
];

class AppSecurityService {
  getStorageKey(userId) {
    return `${STORAGE_PREFIX}${userId || 'default'}`;
  }

  /**
   * Load entire security configuration for a user
   */
  getSecurityConfig(userId) {
    try {
      const raw = localStorage.getItem(this.getStorageKey(userId));
      if (!raw) return { apps: {} };
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : { apps: {} };
    } catch (e) {
      console.error('Failed to read app security config:', e);
      return { apps: {} };
    }
  }

  /**
   * Save security configuration
   */
  saveSecurityConfig(userId, config) {
    try {
      localStorage.setItem(this.getStorageKey(userId), JSON.stringify(config));
      window.dispatchEvent(new CustomEvent('app-security-updated', { detail: { userId, config } }));
    } catch (e) {
      console.error('Failed to save app security config:', e);
    }
  }

  /**
   * Get specific app configuration.
   * Default: security is OFF (enabled: false).
   */
  getAppConfig(userId, slug) {
    const config = this.getSecurityConfig(userId);
    const appConfig = config.apps?.[slug];
    if (!appConfig) {
      return {
        enabled: false,
        primaryMethod: 'biometric',
        methods: {
          biometric: true,
          password: true,
          pin: false,
          pattern: false,
        },
        pinConfig: null,
        patternConfig: null,
        passwordConfig: { type: 'master' },
        autoLockTimeout: 5, // minutes (0 = immediate, -1 = session only)
      };
    }
    return {
      enabled: Boolean(appConfig.enabled),
      primaryMethod: appConfig.primaryMethod || 'biometric',
      methods: {
        biometric: Boolean(appConfig.methods?.biometric ?? true),
        password: Boolean(appConfig.methods?.password ?? true),
        pin: Boolean(appConfig.methods?.pin),
        pattern: Boolean(appConfig.methods?.pattern),
      },
      pinConfig: appConfig.pinConfig || null,
      patternConfig: appConfig.patternConfig || null,
      passwordConfig: appConfig.passwordConfig || { type: 'master' },
      autoLockTimeout: appConfig.autoLockTimeout !== undefined ? appConfig.autoLockTimeout : 5,
    };
  }

  /**
   * Save configuration for a specific app
   */
  saveAppConfig(userId, slug, appConfig) {
    const config = this.getSecurityConfig(userId);
    if (!config.apps) config.apps = {};
    config.apps[slug] = {
      ...appConfig,
      updatedAt: new Date().toISOString(),
    };
    this.saveSecurityConfig(userId, config);
  }

  /**
   * Check if an app has security protection active.
   * Returns false by default.
   */
  isAppProtected(userId, slug) {
    const appConfig = this.getAppConfig(userId, slug);
    return Boolean(appConfig.enabled);
  }

  /**
   * Check if app is currently unlocked in this session and within auto-lock timeout.
   */
  isAppUnlocked(userId, slug) {
    if (!this.isAppProtected(userId, slug)) {
      return true; // Not protected = opens directly
    }

    try {
      const raw = sessionStorage.getItem(`${UNLOCK_PREFIX}${slug}`);
      if (!raw) return false;
      const data = JSON.parse(raw);
      if (!data || !data.unlockedAt) return false;

      const appConfig = this.getAppConfig(userId, slug);
      const timeoutMinutes = appConfig.autoLockTimeout;

      // Timeout: 0 = immediate (re-locks as soon as checked or page reloads)
      if (timeoutMinutes === 0) {
        return false;
      }

      // Timeout: -1 = valid for entire browser session until tab closed
      if (timeoutMinutes === -1) {
        return true;
      }

      // Minutes timeout check
      const elapsedMs = Date.now() - data.unlockedAt;
      const maxMs = (timeoutMinutes || 5) * 60 * 1000;
      if (elapsedMs > maxMs) {
        this.lockApp(slug);
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }

  /**
   * Mark an app as successfully unlocked
   */
  markAppUnlocked(slug) {
    try {
      const payload = {
        unlockedAt: Date.now(),
      };
      sessionStorage.setItem(`${UNLOCK_PREFIX}${slug}`, JSON.stringify(payload));
      // Keep backward compatibility for legacy checks
      sessionStorage.setItem(`pm_app_unlocked_${slug}`, 'true');
      window.dispatchEvent(new CustomEvent('app-unlocked', { detail: { slug } }));
    } catch (e) {
      console.error('Failed to mark app unlocked:', e);
    }
  }

  /**
   * Explicitly lock a single app
   */
  lockApp(slug) {
    try {
      sessionStorage.removeItem(`${UNLOCK_PREFIX}${slug}`);
      sessionStorage.removeItem(`pm_app_unlocked_${slug}`);
      window.dispatchEvent(new CustomEvent('app-locked', { detail: { slug } }));
    } catch (e) {
      console.error('Failed to lock app:', e);
    }
  }

  /**
   * Lock all apps immediately
   */
  lockAllApps() {
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key && (key.startsWith(UNLOCK_PREFIX) || key.startsWith('pm_app_unlocked_'))) {
          sessionStorage.removeItem(key);
        }
      }
      window.dispatchEvent(new CustomEvent('all-apps-locked'));
    } catch (e) {
      console.error('Failed to lock all apps:', e);
    }
  }
}

export const appSecurityService = new AppSecurityService();
export default appSecurityService;
